/**
 * Interactive SVG charts for the admin overview dashboard.
 */
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';

function pct(value, total) {
  if (!total) return 0;
  return Math.round((value / total) * 100);
}

function polarToCartesian(cx, cy, r, angleDeg) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function describeArc(cx, cy, r, startAngle, endAngle) {
  const start = polarToCartesian(cx, cy, r, endAngle);
  const end = polarToCartesian(cx, cy, r, startAngle);
  const largeArc = endAngle - startAngle <= 180 ? 0 : 1;
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 0 ${end.x} ${end.y}`;
}

/** Featured subscription breakdown — donut + stat tiles + horizontal bars */
export function SubscriptionBreakdownChart({ segments, totalUsers }) {
  const [active, setActive] = useState(null);
  const total = useMemo(
    () => segments.reduce((sum, s) => sum + (Number(s.value) || 0), 0),
    [segments]
  );

  const size = 200;
  const cx = size / 2;
  const cy = size / 2;
  const radius = 72;
  const gap = 3;

  const arcs = useMemo(() => {
    let angle = 0;
    return segments.map((seg) => {
      const value = Number(seg.value) || 0;
      const sweep = total ? (value / total) * 360 : 0;
      const start = angle + gap / 2;
      const end = angle + Math.max(sweep - gap, 0);
      const arc = {
        ...seg,
        value,
        percent: pct(value, total),
        start,
        end,
        path: sweep > gap ? describeArc(cx, cy, radius, start, end) : null,
      };
      angle += sweep;
      return arc;
    });
  }, [segments, total, cx, cy, radius]);

  const hovered = arcs.find((a) => a.key === active) || null;
  const focus = hovered || arcs.reduce((best, a) => (a.value > (best?.value || 0) ? a : best), null);

  return (
    <div className="xk-admin__sub-breakdown">
      <div className="xk-admin__chart-head xk-admin__chart-head--featured">
        <div>
          <h3 className="xk-admin__chart-title">Subscription breakdown</h3>
          <p className="xk-admin__chart-desc">
            Distribution across {totalUsers} registered user{totalUsers === 1 ? '' : 's'}
          </p>
        </div>
        {focus && (
          <div className="xk-admin__chart-focus" style={{ borderColor: focus.color }}>
            <span className="xk-admin__chart-focus-label">{focus.label}</span>
            <span className="xk-admin__chart-focus-value" style={{ color: focus.color }}>
              {focus.value}
              <small>{focus.percent}%</small>
            </span>
          </div>
        )}
      </div>

      <div className="xk-admin__sub-breakdown-body">
        <div className="xk-admin__sub-donut-col">
          <svg className="xk-admin__donut xk-admin__donut--lg" viewBox={`0 0 ${size} ${size}`} role="img">
            <circle cx={cx} cy={cy} r={radius} fill="none" stroke="#f3f3f3" strokeWidth="22" />
            {arcs.map((arc) =>
              arc.path ? (
                <path
                  key={arc.key}
                  d={arc.path}
                  fill="none"
                  stroke={arc.color}
                  strokeWidth={active === arc.key ? 26 : 22}
                  strokeLinecap="round"
                  className="xk-admin__donut-arc"
                  onMouseEnter={() => setActive(arc.key)}
                  onMouseLeave={() => setActive(null)}
                  style={{ opacity: active && active !== arc.key ? 0.35 : 1 }}
                />
              ) : null
            )}
            <circle cx={cx} cy={cy} r="46" fill="#fff" />
            <text x={cx} y={cy - 6} textAnchor="middle" className="xk-admin__donut-center-value xk-admin__donut-center-value--lg">
              {hovered ? hovered.value : total}
            </text>
            <text x={cx} y={cy + 14} textAnchor="middle" className="xk-admin__donut-center-label">
              {hovered ? hovered.percent + '%' : 'Total'}
            </text>
          </svg>
        </div>

        <div className="xk-admin__sub-stats-col">
          <div className="xk-admin__sub-tiles">
            {arcs.map((arc) => (
              <button
                key={arc.key}
                type="button"
                className={`xk-admin__sub-tile ${active === arc.key ? 'xk-admin__sub-tile--active' : ''}`}
                style={{ '--tile-color': arc.color }}
                onMouseEnter={() => setActive(arc.key)}
                onMouseLeave={() => setActive(null)}
                onClick={() => setActive(active === arc.key ? null : arc.key)}
              >
                <span className="xk-admin__sub-tile-dot" />
                <span className="xk-admin__sub-tile-label">{arc.label}</span>
                <span className="xk-admin__sub-tile-value">{arc.value}</span>
                <span className="xk-admin__sub-tile-pct">{arc.percent}%</span>
              </button>
            ))}
          </div>

          <div className="xk-admin__h-bar-chart">
            <div className="xk-admin__h-bar-chart-title">Distribution</div>
            {arcs.map((arc) => (
              <div
                key={`bar-${arc.key}`}
                className={`xk-admin__h-bar-row ${active === arc.key ? 'xk-admin__h-bar-row--active' : ''}`}
                onMouseEnter={() => setActive(arc.key)}
                onMouseLeave={() => setActive(null)}
              >
                <span className="xk-admin__h-bar-label">{arc.label}</span>
                <div className="xk-admin__h-bar-track">
                  <div
                    className="xk-admin__h-bar-fill"
                    style={{ width: `${arc.percent}%`, background: arc.color }}
                  />
                </div>
                <span className="xk-admin__h-bar-pct">{arc.percent}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

SubscriptionBreakdownChart.propTypes = {
  segments: PropTypes.arrayOf(PropTypes.object).isRequired,
  totalUsers: PropTypes.number,
};

SubscriptionBreakdownChart.defaultProps = { totalUsers: 0 };

/** Vertical histogram with Y-axis grid */
export function HistogramChart({ data, title, subtitle, color, accentColor }) {
  const [active, setActive] = useState(null);
  const max = useMemo(() => Math.max(1, ...data.map((d) => Number(d.value ?? d.count) || 0)), [data]);
  const total = useMemo(
    () => data.reduce((sum, d) => sum + (Number(d.value ?? d.count) || 0), 0),
    [data]
  );
  const hovered = data.find((d) => d.key === active);
  const gridLines = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(max * f));

  return (
    <div className="xk-admin__chart xk-admin__chart--histogram">
      <div className="xk-admin__chart-head">
        <div>
          <h3 className="xk-admin__chart-title">{title}</h3>
          {subtitle && <p className="xk-admin__chart-desc">{subtitle}</p>}
        </div>
        <span className="xk-admin__chart-meta">
          {hovered
            ? `${hovered.label}: ${hovered.value ?? hovered.count}`
            : `Total: ${total}`}
        </span>
      </div>

      <div className="xk-admin__histogram">
        <div className="xk-admin__histogram-y">
          {[...gridLines].reverse().map((tick) => (
            <span key={tick} className="xk-admin__histogram-tick">{tick}</span>
          ))}
        </div>
        <div className="xk-admin__histogram-body">
          <div className="xk-admin__histogram-grid">
            {gridLines.map((tick) => (
              <div key={`g-${tick}`} className="xk-admin__histogram-gridline" />
            ))}
          </div>
          <div className="xk-admin__histogram-bars">
            {data.map((item) => {
              const val = Number(item.value ?? item.count) || 0;
              const heightPct = (val / max) * 100;
              const isActive = active === item.key;
              return (
                <div
                  key={item.key}
                  className={`xk-admin__histogram-col ${isActive ? 'xk-admin__histogram-col--active' : ''}`}
                  onMouseEnter={() => setActive(item.key)}
                  onMouseLeave={() => setActive(null)}
                >
                  <div className="xk-admin__histogram-tooltip">{val}</div>
                  <div className="xk-admin__histogram-bar-wrap">
                    <div
                      className="xk-admin__histogram-bar"
                      style={{
                        height: `${Math.max(val ? 4 : 0, heightPct)}%`,
                        background: isActive
                          ? `linear-gradient(180deg, ${accentColor || color} 0%, ${color} 100%)`
                          : `linear-gradient(180deg, ${color} 0%, ${color}cc 100%)`,
                      }}
                    />
                  </div>
                  <div className="xk-admin__histogram-label">{item.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

HistogramChart.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  color: PropTypes.string,
  accentColor: PropTypes.string,
};

HistogramChart.defaultProps = {
  subtitle: null,
  color: '#8CC152',
  accentColor: '#DA4453',
};

/** Horizontal histogram for categorical comparison */
export function HorizontalHistogram({ items, title, subtitle }) {
  const [active, setActive] = useState(null);
  const max = useMemo(() => Math.max(1, ...items.map((i) => Number(i.value) || 0)), [items]);

  return (
    <div className="xk-admin__chart">
      <div className="xk-admin__chart-head">
        <div>
          <h3 className="xk-admin__chart-title">{title}</h3>
          {subtitle && <p className="xk-admin__chart-desc">{subtitle}</p>}
        </div>
      </div>
      <div className="xk-admin__h-histogram">
        {items.map((item) => {
          const value = Number(item.value) || 0;
          const widthPct = (value / max) * 100;
          const isActive = active === item.key;
          return (
            <div
              key={item.key}
              className={`xk-admin__h-hist-row ${isActive ? 'xk-admin__h-hist-row--active' : ''}`}
              onMouseEnter={() => setActive(item.key)}
              onMouseLeave={() => setActive(null)}
            >
              <div className="xk-admin__h-hist-label-wrap">
                <span className="xk-admin__h-hist-swatch" style={{ background: item.color }} />
                <span className="xk-admin__h-hist-label">{item.label}</span>
              </div>
              <div className="xk-admin__h-hist-bar-area">
                <div className="xk-admin__h-hist-track">
                  <div
                    className="xk-admin__h-hist-fill"
                    style={{
                      width: `${widthPct}%`,
                      background: `linear-gradient(90deg, ${item.color} 0%, ${item.color}99 100%)`,
                    }}
                  />
                </div>
                <span className="xk-admin__h-hist-value">{value}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

HorizontalHistogram.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
};

HorizontalHistogram.defaultProps = { subtitle: null };

/** Horizontal progress bars */
export function ProgressBars({ items, title, subtitle }) {
  const [active, setActive] = useState(null);
  const total = useMemo(
    () => items.reduce((sum, i) => sum + (Number(i.value) || 0), 0),
    [items]
  );

  return (
    <div className="xk-admin__chart">
      <div className="xk-admin__chart-head">
        <div>
          <h3 className="xk-admin__chart-title">{title}</h3>
          {subtitle && <p className="xk-admin__chart-desc">{subtitle}</p>}
        </div>
      </div>
      <ul className="xk-admin__progress-list">
        {items.map((item) => {
          const value = Number(item.value) || 0;
          const width = total ? (value / total) * 100 : 0;
          return (
            <li
              key={item.key}
              className={`xk-admin__progress-row ${active === item.key ? 'xk-admin__progress-row--active' : ''}`}
              onMouseEnter={() => setActive(item.key)}
              onMouseLeave={() => setActive(null)}
            >
              <div className="xk-admin__progress-meta">
                <span className="xk-admin__progress-label">
                  <span className="xk-admin__progress-dot" style={{ background: item.color }} />
                  {item.label}
                </span>
                <span className="xk-admin__progress-count">
                  {value} <em>({pct(value, total)}%)</em>
                </span>
              </div>
              <div className="xk-admin__progress-track">
                <div
                  className="xk-admin__progress-fill"
                  style={{ width: `${width}%`, background: item.color }}
                />
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

ProgressBars.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
};

ProgressBars.defaultProps = { subtitle: null };

/** Tiny sparkline for KPI cards */
export function MiniSparkline({ points, color }) {
  const values = (points || []).map((p) => Number(p.count ?? p.value) || 0);
  const w = 88;
  const h = 28;
  const max = Math.max(1, ...values);
  const step = values.length > 1 ? w / (values.length - 1) : w;
  const coords = values.map((v, i) => {
    const x = i * step;
    const y = h - (v / max) * (h - 4) - 2;
    return `${x},${y}`;
  });
  const line = coords.join(' ');
  const area = values.length ? `0,${h} ${line} ${w},${h}` : '';

  return (
    <svg className="xk-admin__spark" viewBox={`0 0 ${w} ${h}`} width={w} height={h} aria-hidden="true">
      <polygon points={area} fill={color} opacity="0.16" />
      <polyline
        points={line}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

MiniSparkline.propTypes = {
  points: PropTypes.arrayOf(PropTypes.object),
  color: PropTypes.string,
};

MiniSparkline.defaultProps = { points: [], color: '#8CC152' };

/** Circular progress ring behind a KPI icon */
export function StatRing({ percent, color, children }) {
  const size = 44;
  const stroke = 3.5;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const p = Math.max(0, Math.min(100, Number(percent) || 0));
  const dash = (p / 100) * c;

  return (
    <span className="xk-admin__stat-ring">
      <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} aria-hidden="true">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#ececec" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${c - dash}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      {children}
    </span>
  );
}

StatRing.propTypes = {
  percent: PropTypes.number,
  color: PropTypes.string,
  children: PropTypes.node,
};

StatRing.defaultProps = { percent: 0, color: '#8CC152', children: null };
